import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, message, Typography, Spin, InputNumber, Upload, Select } from 'antd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { personalsApi, uploadApi } from '../../services/api';

const { Title } = Typography;

const PersonalsForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // eslint-disable-next-line 
  const [coverUrl, setCoverUrl] = useState('');
  // eslint-disable-next-line
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  // 类型选项
  const typeOptions = [
    { value: 'イベント', label: 'イベント' },
    { value: 'お知らせ', label: 'お知らせ' },
    { value: 'メディア', label: 'メディア' },
    { value: '試合結果', label: '試合結果' },
    { value: '特集', label: '特集' },
  ];

  const fetchPersonalDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await personalsApi.getById(id);
      const coverImageUrl = response.data.cover;
      form.setFieldsValue({
        title: response.data.title,
        cover: coverImageUrl,
        content: response.data.content,
        sort: response.data.sort,
        type: response.data.type || 'イベント', // 设置类型字段
      });
      if (coverImageUrl) {
        setCoverUrl(coverImageUrl);
        // 初始化文件列表
        setFileList([
          {
            uid: '-1',
            name: 'image.png',
            status: 'done',
            url: coverImageUrl,
          },
        ]);
      }
    } catch (error) {
      message.error('個人ニュース詳細の取得に失敗しました：' + (error.response?.data?.message || 'ネットワークエラー'));
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  useEffect(() => {
    if (isEdit) {
      fetchPersonalDetail();
    }
  }, [isEdit, fetchPersonalDetail]);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {      
      // 确保cover字段是字符串类型
      // 直接使用coverUrl状态，这是最可靠的URL来源
      values.cover = coverUrl || '';
            
      if (isEdit) {
        await personalsApi.update(id, values);
        message.success('更新しました');
      } else {
        await personalsApi.create(values);
        message.success('作成しました');
      }
      navigate('/personals');
    } catch (error) {
      message.error((isEdit ? '更新' : '作成') + 'に失敗しました：' + (error.response?.data?.message || 'ネットワークエラー'));
    } finally {
      setSubmitting(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const response = await uploadApi.uploadImage(file);
      const url = response.data;
      setCoverUrl(url);
      form.setFieldsValue({ cover: url });
      // 更新文件列表
      setFileList([
        {
          uid: file.uid || '-1',
          name: file.name || 'image.png',
          status: 'done',
          url: url,
        },
      ]);
      onSuccess?.();
      message.success('画像アップロード成功');
    } catch (error) {
      // 更新文件列表状态为error
      setFileList([
        {
          uid: file.uid || '-1',
          name: file.name || 'image.png',
          status: 'error',
        },
      ]);
      onError?.(error);
      message.error('画像アップロード失敗：' + (error.message || error.response?.data?.message || 'ネットワークエラー'));
    } finally {
      setUploading(false);
    }
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length === 0) {
      setCoverUrl('');
      form.setFieldsValue({ cover: '' });
    }
  };

  const handlePreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  return (
    <Card>
      <Title level={4}>{isEdit ? '個人ニュース編集' : '個人ニュース作成'}</Title>
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item
            label="タイトル"
            name="title"
            rules={[{ required: true, message: 'タイトルを入力してください!' }]}
          >
            <Input placeholder="個人ニュースのタイトルを入力してください" />
          </Form.Item>
          
          <Form.Item
            label="タイプ"
            name="type"
            initialValue="イベント"
            rules={[{ required: true, message: 'タイプを選択してください!' }]}
          >
            <Select
              placeholder="タイプを選択"
              options={typeOptions}
            />
          </Form.Item>
          
          <Form.Item
            label="カバー画像"
          >
            <Upload
              customRequest={handleUpload}
              listType="picture-card"
              fileList={fileList}
              onChange={handleChange}
              onPreview={handlePreview}
              maxCount={1}
            >
              {fileList.length < 1 && '+ Upload'}
            </Upload>
          </Form.Item>

          <Form.Item
            label="ソート"
            name="sort"
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="ソート値を入力してください" />
          </Form.Item>

          <Form.Item
            label="内容"
            name="content"
            rules={[{ required: true, message: '内容を入力してください!' }]}
          >
            <ReactQuill 
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{'list': 'ordered'}, {'list': 'bullet'}],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
              style={{ background: 'white' }}
              placeholder="個人ニュースの内容を入力してください"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {isEdit ? '更新' : '作成'}
            </Button>
            <Button 
              style={{ marginLeft: 8 }}
              onClick={() => navigate('/personals')}
            >
              キャンセル
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default PersonalsForm;
